import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Cloud, Server, Shield, Lock, Network, Database, Key,
  CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowLeft,
  ChevronLeft, ChevronRight, Rocket, Eye, EyeOff, Globe,
} from "lucide-react";
import { toast } from "sonner";
import {
  DataPlaneSelector,
  RegionSelector,
  DeploymentProgress,
  UpgradePrompt,
  DriftAlertCard,
} from "@/components/EnterpriseComponents";

const STEPS = [
  { id: "choose", label: "Data Plane", icon: Cloud, desc: "Select hosting option" },
  { id: "configure", label: "Azure Setup", icon: Key, desc: "OAuth & configuration" },
  { id: "deploy", label: "Deploy", icon: Rocket, desc: "Review & provision" },
];

export default function EnterpriseDataPlanePage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [regions, setRegions] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(null);
  const [existingDataPlanes, setExistingDataPlanes] = useState([]);

  const [form, setForm] = useState({
    hosting_type: null, // "niyant_hosted" | "customer_hosted"
    name: "",
    tenant_id: "",
    subscription_id: "",
    client_id: "",
    client_secret: "",
    region: "eastus",
    resource_group_name: "",
  });

  // Check eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const res = await api().get("/enterprise/eligibility");
        setEligibility(res.data);
      } catch (err) {
        setEligibility({ eligible: false, reason: "Unable to check eligibility" });
      } finally {
        setLoading(false);
      }
    };
    checkEligibility();
  }, [api]);

  // Load regions
  useEffect(() => {
    api().get("/deployments/regions")
      .then(r => setRegions(r.data.regions || []))
      .catch(() => {});
  }, [api]);

  // Load existing data planes
  useEffect(() => {
    api().get("/enterprise/data-planes")
      .then(r => setExistingDataPlanes(r.data.data_planes || []))
      .catch(() => {});
  }, [api]);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDataPlaneSelect = (type) => {
    updateForm("hosting_type", type);
    if (type === "niyant_hosted") {
      // For Niyant hosted, skip Azure config
      toast.success("Using Niyant Hosted data plane");
      navigate("/");
    } else {
      setStep(1);
    }
  };

  const handleOAuthConsent = async () => {
    setDeploying(true);
    try {
      // Start OAuth flow for Azure consent
      const res = await api().post("/enterprise/oauth/start", {
        tenant_id: form.tenant_id,
        subscription_id: form.subscription_id,
      });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start OAuth flow");
    } finally {
      setDeploying(false);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setDeploymentProgress({ status: "initializing", message: "Starting deployment...", percent: 0 });
    
    try {
      // Step 1: Create data plane record
      setDeploymentProgress({ status: "creating", message: "Creating data plane configuration...", percent: 10 });
      const createRes = await api().post("/enterprise/data-planes", {
        name: form.name,
        azure_config: {
          tenant_id: form.tenant_id,
          subscription_id: form.subscription_id,
          client_id: form.client_id,
          client_secret: form.client_secret,
          region: form.region,
          resource_group_name: form.resource_group_name,
        },
      });
      const dataPlaneId = createRes.data.id;

      // Step 2: Generate ARM template
      setDeploymentProgress({ status: "generating", message: "Generating ARM template...", percent: 30 });
      await api().post(`/enterprise/data-planes/${dataPlaneId}/generate-template`);

      // Step 3: Deploy to Azure
      setDeploymentProgress({ status: "deploying", message: "Deploying to Azure...", percent: 50 });
      const deployRes = await api().post(`/enterprise/data-planes/${dataPlaneId}/deploy`);

      if (deployRes.data.status === "deployed") {
        setDeploymentProgress({ status: "success", message: "Data plane deployed successfully!", percent: 100 });
        toast.success("Enterprise data plane deployed successfully!");
        setTimeout(() => navigate("/"), 2000);
      } else {
        setDeploymentProgress({ 
          status: "in_progress", 
          message: "Deployment in progress. Check back in a few minutes.", 
          percent: 70 
        });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Deployment failed";
      setDeploymentProgress({ status: "error", message: msg, percent: 0 });
      toast.error(msg);
    } finally {
      setDeploying(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return form.name && form.tenant_id && form.subscription_id;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--hig-accent)]" />
      </div>
    );
  }

  return (
    <div className="animate-page-enter max-w-4xl mx-auto" data-testid="enterprise-data-plane-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => step === 0 ? navigate("/") : setStep(step - 1)} 
          className="text-xs text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]"
          data-testid="enterprise-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> {step === 0 ? "Home" : "Back"}
        </Button>
        <span className="text-[var(--n-border-3)]">/</span>
        <span className="text-sm font-medium">Enterprise Data Plane</span>
      </div>

      {/* Step indicator */}
      {form.hosting_type === "customer_hosted" && (
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap shrink-0 transition-all ${
                  i === step ? "bg-[var(--hig-accent)] text-white font-medium" :
                  i < step ? "bg-emerald-500/10 text-emerald-600" :
                  "bg-[var(--n-bg)] text-[var(--n-text-muted)]"
                }`}
                data-testid={`enterprise-step-${s.id}`}
              >
                {i < step ? <CheckCircle2 className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-[var(--n-text-muted)] shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="space-y-6">
        {/* Step 0: Data Plane Selection */}
        {step === 0 && (
          <DataPlaneSelector
            onSelect={handleDataPlaneSelect}
            eligibility={eligibility}
            loading={false}
          />
        )}

        {/* Step 1: Azure Configuration */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-azure-config">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Azure Configuration</h2>
              <p className="text-sm text-[var(--n-text-muted)] mt-1">
                Configure your Azure tenant for the enterprise data plane deployment.
              </p>
            </div>

            <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
              <CardContent className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Data Plane Name *</Label>
                  <Input 
                    value={form.name} 
                    onChange={e => updateForm("name", e.target.value)} 
                    placeholder="e.g. my-enterprise-data" 
                    className="bg-[var(--n-bg)] border-[var(--n-border-2)]" 
                    data-testid="input-dp-name" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Tenant ID *</Label>
                    <Input 
                      value={form.tenant_id} 
                      onChange={e => updateForm("tenant_id", e.target.value)} 
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" 
                      className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" 
                      data-testid="input-dp-tenant" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Subscription ID *</Label>
                    <Input 
                      value={form.subscription_id} 
                      onChange={e => updateForm("subscription_id", e.target.value)} 
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" 
                      className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" 
                      data-testid="input-dp-subscription" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Client ID <span className="text-[var(--n-text-muted)] font-normal">(Service Principal)</span></Label>
                    <Input 
                      value={form.client_id} 
                      onChange={e => updateForm("client_id", e.target.value)} 
                      placeholder="Service Principal App ID" 
                      className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm" 
                      data-testid="input-dp-client-id" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Client Secret</Label>
                    <div className="relative">
                      <Input 
                        type={showSecret ? "text" : "password"} 
                        value={form.client_secret} 
                        onChange={e => updateForm("client_secret", e.target.value)} 
                        placeholder="Service Principal secret" 
                        className="bg-[var(--n-bg)] border-[var(--n-border-2)] font-mono text-sm pr-9" 
                        data-testid="input-dp-client-secret" 
                      />
                      <button 
                        onClick={() => setShowSecret(!showSecret)} 
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--n-text-muted)]"
                      >
                        {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Region</Label>
                    <RegionSelector
                      value={form.region}
                      onChange={(v) => updateForm("region", v)}
                      regions={regions}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Resource Group Name</Label>
                    <Input 
                      value={form.resource_group_name} 
                      onChange={e => updateForm("resource_group_name", e.target.value)} 
                      placeholder={`rg-niyant-data-${form.region || 'region'}`}
                      className="bg-[var(--n-bg)] border-[var(--n-border-2)]" 
                      data-testid="input-dp-rg" 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* OAuth Option */}
            <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      Azure OAuth Consent
                    </h3>
                    <p className="text-xs text-[var(--n-text-muted)] mt-1">
                      Use OAuth to grant Niyant access without sharing credentials.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleOAuthConsent}
                    disabled={!form.tenant_id || !form.subscription_id || deploying}
                    data-testid="oauth-consent-btn"
                  >
                    {deploying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Key className="w-4 h-4 mr-1.5" />}
                    Start OAuth Flow
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-5 border-t border-[var(--n-border)]">
              <Button 
                variant="ghost" 
                onClick={() => setStep(0)} 
                className="text-sm" 
                data-testid="enterprise-back-step-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!canProceed()} 
                className="text-sm bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent-hover)]"
                data-testid="enterprise-next-btn"
              >
                Review & Deploy <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Deploy */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-deploy">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Review & Deploy</h2>
              <p className="text-sm text-[var(--n-text-muted)] mt-1">
                Review your configuration before deploying the enterprise data plane.
              </p>
            </div>

            {/* Summary */}
            <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
              <CardContent className="p-5">
                <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                  <Database className="w-4 h-4 text-[var(--hig-accent)]" />
                  Data Plane Summary
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><span className="text-[var(--n-text-muted)]">Name:</span> <span className="font-medium">{form.name || "—"}</span></div>
                  <div><span className="text-[var(--n-text-muted)]">Hosting:</span> <Badge variant="outline" className="text-xs">Customer Hosted</Badge></div>
                  <div><span className="text-[var(--n-text-muted)]">Tenant:</span> <span className="font-mono text-xs">{form.tenant_id || "—"}</span></div>
                  <div><span className="text-[var(--n-text-muted)]">Subscription:</span> <span className="font-mono text-xs">{form.subscription_id || "—"}</span></div>
                  <div><span className="text-[var(--n-text-muted)]">Region:</span> <span className="font-medium">{regions.find(r => r.value === form.region)?.label || form.region}</span></div>
                  <div><span className="text-[var(--n-text-muted)]">Resource Group:</span> <span className="font-mono text-xs">{form.resource_group_name || `rg-niyant-data-${form.region}`}</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Resources to be deployed */}
            <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
              <CardContent className="p-5">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Server className="w-4 h-4 text-emerald-500" />
                  Resources to Deploy
                </h3>
                <ul className="space-y-2 text-sm">
                  {[
                    { name: "Cosmos DB (MongoDB API)", desc: "Primary data store for scanned resources", icon: Database },
                    { name: "Private Endpoint", desc: "Secure VNet connectivity", icon: Lock },
                    { name: "Key Vault", desc: "Secure credential storage", icon: Key },
                    { name: "Log Analytics Workspace", desc: "Monitoring and diagnostics", icon: Globe },
                  ].map((resource, i) => (
                    <li key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[var(--n-bg)]">
                      <resource.icon className="w-4 h-4 text-[var(--hig-accent)]" />
                      <div>
                        <span className="font-medium">{resource.name}</span>
                        <span className="text-xs text-[var(--n-text-muted)] ml-2">{resource.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Deployment Progress */}
            {deploymentProgress && (
              <DeploymentProgress
                status={deploymentProgress.status}
                message={deploymentProgress.message}
                percent={deploymentProgress.percent}
              />
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-5 border-t border-[var(--n-border)]">
              <Button 
                variant="ghost" 
                onClick={() => setStep(1)} 
                className="text-sm"
                disabled={deploying}
                data-testid="enterprise-deploy-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button 
                onClick={handleDeploy} 
                disabled={deploying || deploymentProgress?.status === "success"} 
                className="text-sm bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="enterprise-deploy-btn"
              >
                {deploying ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Deploying...</>
                ) : (
                  <><Rocket className="w-4 h-4 mr-1.5" /> Deploy Data Plane</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Existing Data Planes */}
        {step === 0 && existingDataPlanes.length > 0 && (
          <Card className="bg-[var(--n-surface)] border-[var(--n-border)]">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Database className="w-4 h-4 text-[var(--hig-accent)]" />
                Your Data Planes
              </h3>
              <div className="space-y-2">
                {existingDataPlanes.map((dp) => (
                  <div 
                    key={dp.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border)]"
                    data-testid={`data-plane-${dp.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {dp.hosting_type === "niyant_hosted" ? (
                        <Cloud className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Server className="w-4 h-4 text-emerald-500" />
                      )}
                      <div>
                        <span className="text-sm font-medium">{dp.name}</span>
                        <span className="text-xs text-[var(--n-text-muted)] ml-2">
                          {dp.hosting_type === "niyant_hosted" ? "Niyant Hosted" : "Customer Hosted"}
                        </span>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        dp.status === "deployed" ? "bg-emerald-500/10 text-emerald-500" :
                        dp.status === "drift_detected" ? "bg-amber-500/10 text-amber-500" :
                        "bg-gray-500/10 text-gray-500"
                      }`}
                    >
                      {dp.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Drift Alerts */}
        {step === 0 && existingDataPlanes.some(dp => dp.status === "drift_detected") && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Drift Alerts
            </h3>
            {existingDataPlanes
              .filter(dp => dp.status === "drift_detected")
              .map(dp => (
                <DriftAlertCard
                  key={dp.id}
                  alert={{
                    data_plane_id: dp.id,
                    data_plane_name: dp.name,
                    drift_type: "resource_drift",
                    severity: "warning",
                    message: "Configuration drift detected. Review and remediate.",
                    detected_at: dp.updated_at,
                  }}
                  onDismiss={() => {}}
                  onRemediate={() => navigate(`/enterprise/data-planes/${dp.id}`)}
                />
              ))}
          </div>
        )}

        {/* Upgrade Prompt for non-eligible users */}
        {!eligibility?.eligible && step === 0 && (
          <UpgradePrompt
            currentPlan={eligibility?.current_plan || "free"}
            requiredPlan="enterprise"
            feature="Customer-hosted data planes"
          />
        )}
      </div>
    </div>
  );
}
